import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Search } from 'lucide-react';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Badge,
  Input,
  Page,
  PageContent,
  DataTable
} from '@bliks-io/ui';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useRecordingDashboard } from '@/api/queries/useRecordingDashboard';
import { useReassignRecording } from '@/api/mutations/useRecording';
import { useRejectAssignment } from '@/api/mutations/useAssignments';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { recordingDashboardKeys } from '@/api/query-keys';
import { ReassignContextDialog } from '@/components/processes/ReassignContextDialog';
import { QueryErrorRetry } from '@/components/query-error-retry/QueryErrorRetry';
import type { RecordingDashboardFlatStep } from '@bliks-io/contracts';
import type { SuccessorUpdateData } from '@/pages/processes/recording/types';
import { toReassignPayload } from '@/lib/successorEntityMapping';
import { PROCESS_STATUS_COLOR_SCHEME } from '@/lib/process-utils';
import { RecordingProgressBar } from './RecordingProgressBar';
import { NudgeDialog } from './NudgeDialog';
import { ProcessSubTable } from './ProcessSubTable';
import { useNotStartedColumns } from './hooks/useNotStartedColumns';
import {
  useInRecordingProcessColumns,
  deriveProcessRows
} from './hooks/useInRecordingProcessColumns';
import type { NudgeTarget } from './types';

export function RecordingDashboardPage() {
  const { t } = useTranslation();
  useDocumentTitle('recordingDashboard.title');

  const { data, isLoading, isError, isFetching, refetch } = useRecordingDashboard();
  const queryClient = useQueryClient();
  const reassignMutation = useReassignRecording();
  const rejectMutation = useRejectAssignment();

  const [globalFilter, setGlobalFilter] = useState('');

  // Nudge state
  const [nudgeTarget, setNudgeTarget] = useState<NudgeTarget | null>(null);

  // Reassign state
  const [reassignTarget, setReassignTarget] = useState<{
    step: RecordingDashboardFlatStep;
  } | null>(null);

  const handleNudge = useCallback((target: NudgeTarget) => {
    setNudgeTarget(target);
  }, []);

  const handleReassign = useCallback((step: RecordingDashboardFlatStep) => {
    setReassignTarget({ step });
  }, []);

  const handleReassignSubmit = (data: SuccessorUpdateData) => {
    if (!reassignTarget) return;
    const intermediate = toReassignPayload(data);
    if (!intermediate) return;
    const payload = {
      rejectReason: intermediate.isUnclear ? ('unclear' as const) : ('re-assign' as const),
      reAssigneeId: intermediate.reAssigneeId,
      reAssigneeModel: intermediate.reAssigneeModel,
      reAssigneeType: intermediate.reAssigneeType
    };

    const onSuccess = () => {
      toast.success(t('common.success'));
      queryClient.invalidateQueries({
        queryKey: recordingDashboardKeys.all
      });
      setReassignTarget(null);
    };
    const onError = (error: unknown) => toastApiError(error, 'stepRecording.reassignError');

    if (reassignTarget.step.assignmentId && !reassignTarget.step.recordingId) {
      rejectMutation.mutate(
        {
          assignmentId: reassignTarget.step.assignmentId,
          processId: reassignTarget.step.processId,
          data: payload
        },
        { onSuccess, onError }
      );
    } else {
      reassignMutation.mutate(
        {
          recordingId: reassignTarget.step.recordingId!,
          data: payload
        },
        { onSuccess, onError }
      );
    }
  };

  const notStartedColumns = useNotStartedColumns({ onNudge: handleNudge });
  const processColumns = useInRecordingProcessColumns();

  const processRows = useMemo(
    () => deriveProcessRows(data?.inRecordingSteps ?? [], data?.surveys ?? []),
    [data?.inRecordingSteps, data?.surveys]
  );

  const completedRows = useMemo(
    () => deriveProcessRows(data?.completedSteps ?? [], data?.completedSurveys ?? []),
    [data?.completedSteps, data?.completedSurveys]
  );

  const header = (
    <div>
      <h1 className="text-2xl font-bold">{t('recordingDashboard.title')}</h1>
      <p className="text-sm text-muted-foreground">{t('recordingDashboard.subtitle')}</p>
    </div>
  );

  if (isLoading) {
    return (
      <Page>
        <PageContent>
          <div className="space-y-6">
            {header}
            <div className="h-10 animate-pulse rounded-full bg-muted" />
            <div className="h-[300px] animate-pulse rounded-lg border bg-muted" />
          </div>
        </PageContent>
      </Page>
    );
  }

  if (isError || !data) {
    return (
      <Page>
        <PageContent>
          <QueryErrorRetry onRetry={refetch} loading={isFetching} className="min-h-[400px]" />
        </PageContent>
      </Page>
    );
  }

  const { summary, notStartedProcesses } = data;

  const processTabs = [
    {
      value: 'inRecording',
      rows: processRows,
      emptyMessage: t('recordingDashboard.inRecording.noProcesses')
    },
    {
      value: 'completed',
      rows: completedRows,
      emptyMessage: t('recordingDashboard.completed.noProcesses')
    }
  ];

  return (
    <Page>
      <PageContent>
        <div className="space-y-6">
          {header}

          <RecordingProgressBar summary={summary} />

          <Tabs defaultValue={notStartedProcesses.length > 0 ? 'notStarted' : 'inRecording'}>
            <div className="flex items-center justify-between gap-4 my-4">
              <TabsList>
                <TabsTrigger
                  value="notStarted"
                  className="gap-2 data-[state=active]:text-foreground"
                >
                  {t('recordingDashboard.tabs.notStarted')}
                  <Badge colorScheme={PROCESS_STATUS_COLOR_SCHEME.not_started} size="sm">
                    {summary.notStartedCount}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger
                  value="inRecording"
                  className="gap-2 data-[state=active]:text-yellow-primary"
                >
                  {t('recordingDashboard.tabs.inRecording')}
                  <Badge colorScheme={PROCESS_STATUS_COLOR_SCHEME.in_recording} size="sm">
                    {summary.inRecordingCount}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger
                  value="completed"
                  className="gap-2 data-[state=active]:text-green-primary"
                >
                  {t('recordingDashboard.tabs.completed')}
                  <Badge colorScheme={PROCESS_STATUS_COLOR_SCHEME.completed} size="sm">
                    {summary.completedCount}
                  </Badge>
                </TabsTrigger>
              </TabsList>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('dataTable.search')}
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <TabsContent value="notStarted">
              <DataTable
                columns={notStartedColumns}
                data={notStartedProcesses}
                enableSorting
                enableGlobalFilter
                enablePagination
                globalFilter={globalFilter}
                onGlobalFilterChange={setGlobalFilter}
                emptyMessage={t('recordingDashboard.notStarted.noProcesses')}
              />
            </TabsContent>

            {processTabs.map(({ value, rows, emptyMessage }) => (
              <TabsContent key={value} value={value}>
                <DataTable
                  columns={processColumns}
                  data={rows}
                  enableSorting
                  enableGlobalFilter
                  enablePagination
                  enableExpanding
                  getRowCanExpand={(row) =>
                    row.original.steps.length > 0 || row.original.surveys.length > 0
                  }
                  onRowClick={(row) => {
                    if (row.getCanExpand()) row.toggleExpanded();
                  }}
                  globalFilter={globalFilter}
                  onGlobalFilterChange={setGlobalFilter}
                  renderSubComponent={({ row }) => (
                    <ProcessSubTable
                      steps={row.original.steps}
                      surveys={row.original.surveys}
                      onNudge={handleNudge}
                      onReassign={handleReassign}
                    />
                  )}
                  emptyMessage={emptyMessage}
                />
              </TabsContent>
            ))}
          </Tabs>
        </div>

        {nudgeTarget && (
          <NudgeDialog
            onClose={() => setNudgeTarget(null)}
            targetUserId={nudgeTarget.userId}
            targetName={nudgeTarget.name}
            processId={nudgeTarget.processId}
            processName={nudgeTarget.processName}
            stepId={nudgeTarget.stepId}
          />
        )}

        <ReassignContextDialog
          open={!!reassignTarget}
          onOpenChange={(open) => {
            if (!open) setReassignTarget(null);
          }}
          onSubmit={handleReassignSubmit}
          title={t('recordingDashboard.inRecording.reassign')}
          description={reassignTarget?.step.processName ?? ''}
          processId={reassignTarget?.step.processId}
          recordingId={reassignTarget?.step.recordingId}
          hideExternalContact={reassignTarget?.step.isStartStep}
          isSubmitting={reassignMutation.isPending || rejectMutation.isPending}
        />
      </PageContent>
    </Page>
  );
}
